import { reactive } from "vue";
import { useStore } from "vuex";
import {
  useFieldService,
  DEFAULT_FIELD_SERVICE_INFO,
  type FieldServiceInfo,
} from "./useFieldService";
import {
  useDropdownLoaders,
  siteProjectFromAny,
  equipmentProjectFromAny,
} from "./useDropdownLoaders";
import type {
  SiteItem,
  FleetItem,
  WorkOrderItem,
  OverheadTaskItem,
  ApiError,
} from "./types";
import { createConditionalValidator } from "./types";

type RentalTaskInfo = {
  overHead: boolean;
  siteProjectId?: string;
  siteName?: string;
  companyId?: string;
  assetProjectId?: string;
  assetName?: string;
  workOrderProjectId?: string;
  workOrderName?: string;
  workOrderDescription?: string;
  npCode?: string;
  npCodeName?: string;
  comments: string;
} & FieldServiceInfo;

type RentalTaskState = {
  taskInfo: RentalTaskInfo;
  dropdownSite: SiteItem[];
  dropdownFleet: FleetItem[];
  dropdownWorkOrder: WorkOrderItem[];
  dropdownWorkOrderTask: OverheadTaskItem[];
  dropdownProjectId: any[];
  loadingSite: boolean;
  loadingFleet: boolean;
  loadingWorkOrder: boolean;
  loadingWorkOrderTask: boolean;
  error: ApiError | null;
};

export default function useRentalTaskDetail() {
  const store = useStore();
  const dropdownLoaders = useDropdownLoaders("rental");

  const DEFAULT_TASK_INFO: RentalTaskInfo = {
    overHead: false,
    siteProjectId: undefined,
    siteName: undefined,
    companyId: undefined,
    assetProjectId: undefined,
    assetName: undefined,
    workOrderProjectId: undefined,
    workOrderName: undefined,
    workOrderDescription: undefined,
    npCode: undefined,
    npCodeName: undefined,
    comments: "",
    ...DEFAULT_FIELD_SERVICE_INFO,
  };

  const state = reactive<RentalTaskState>({
    taskInfo: { ...DEFAULT_TASK_INFO },
    dropdownSite: [],
    dropdownFleet: [],
    dropdownWorkOrder: [],
    dropdownWorkOrderTask: [],
    dropdownProjectId: [],
    loadingSite: false,
    loadingFleet: false,
    loadingWorkOrder: false,
    loadingWorkOrderTask: false,
    error: null,
  });
  const loadSitesFromApi = async () => {
    state.loadingSite = true;
    try {
      const results = await dropdownLoaders.loadSites();
      const currentId = state.taskInfo.siteProjectId;
      if (currentId && !results.some((s) => s.siteProjectId === currentId)) {
        // Check if we already have an injected item with a proper name
        const existingInjected = state.dropdownSite.find(s => s.siteProjectId === currentId);
        const currentItem = (existingInjected && existingInjected.siteName && existingInjected.siteName !== currentId)
          ? existingInjected
          : {
              siteProjectId: currentId,
              siteName: state.taskInfo.siteName || currentId,
              companyId: state.taskInfo.companyId,
            };
        state.dropdownSite = [currentItem, ...results];
      } else {
        state.dropdownSite = results;
      }
    } finally {
      state.loadingSite = false;
    }
  };

  const searchSites = async (query: string) => {
    state.loadingSite = true;
    try {
      const results = await dropdownLoaders.searchSites(query);
      const currentId = state.taskInfo.siteProjectId;
      if (currentId && !results.some((s) => s.siteProjectId === currentId)) {
        const existingInjected = state.dropdownSite.find(s => s.siteProjectId === currentId);
        const currentItem = (existingInjected && existingInjected.siteName && existingInjected.siteName !== currentId)
          ? existingInjected
          : {
              siteProjectId: currentId,
              siteName: state.taskInfo.siteName || currentId,
              companyId: state.taskInfo.companyId,
            };
        state.dropdownSite = [currentItem, ...results];
      } else {
        state.dropdownSite = results;
      }
    } finally {
      state.loadingSite = false;
    }
  };

  const loadEquipmentsFromApi = async (siteProjectId?: string) => {
    const sid = siteProjectId || state.taskInfo.siteProjectId;
    state.loadingFleet = true;
    try {
      const results = await dropdownLoaders.loadAssets(sid);
      const currentId = state.taskInfo.assetProjectId;
      if (
        currentId &&
        !results.some((f: any) => f.assetProjectId === currentId)
      ) {
        const existingInjected = state.dropdownFleet.find(f => f.assetProjectId === currentId);
        const currentItem = (existingInjected && existingInjected.assetName && existingInjected.assetName !== currentId)
          ? existingInjected
          : {
              assetProjectId: currentId,
              assetName: state.taskInfo.assetName || currentId,
            };
        state.dropdownFleet = [currentItem, ...results];
      } else {
        state.dropdownFleet = results;
      }
    } finally {
      state.loadingFleet = false;
    }
  };

  const searchEquipments = async (query: string) => {
    state.loadingFleet = true;
    try {
      const results = await dropdownLoaders.searchAssets(
        query,
        state.taskInfo.siteProjectId,
      );
      const currentId = state.taskInfo.assetProjectId;
      if (
        currentId &&
        !results.some((f: any) => f.assetProjectId === currentId)
      ) {
        const existingInjected = state.dropdownFleet.find(f => f.assetProjectId === currentId);
        const currentItem = (existingInjected && existingInjected.assetName && existingInjected.assetName !== currentId)
          ? existingInjected
          : {
              assetProjectId: currentId,
              assetName: state.taskInfo.assetName || currentId,
            };
        state.dropdownFleet = [currentItem, ...results];
      } else {
        state.dropdownFleet = results;
      }
    } finally {
      state.loadingFleet = false;
    }
  };

  const loadWorkOrdersFromApi = async (assetProjectId?: string) => {
    const aid = assetProjectId || state.taskInfo.assetProjectId;
    state.loadingWorkOrder = true;
    try {
      const results = await dropdownLoaders.loadWorkOrdersByAsset(aid);
      const currentId = state.taskInfo.workOrderProjectId;
      if (
        currentId &&
        !results.some((w: any) => w.workOrderProjectId === currentId)
      ) {
        const existingInjected = state.dropdownWorkOrder.find(w => w.workOrderProjectId === currentId);
        const currentItem = (existingInjected && existingInjected.workOrderName && existingInjected.workOrderName !== currentId)
          ? existingInjected
          : {
              workOrderProjectId: currentId,
              workOrderName: state.taskInfo.workOrderName || currentId,
            };
        state.dropdownWorkOrder = [currentItem, ...results];
      } else {
        state.dropdownWorkOrder = results;
      }
    } finally {
      state.loadingWorkOrder = false;
    }
  };

  const searchWorkOrders = async (query: string) => {
    state.loadingWorkOrder = true;
    try {
      const results = await dropdownLoaders.searchWorkOrdersByAsset(
        query,
        state.taskInfo.assetProjectId,
      );
      const currentId = state.taskInfo.workOrderProjectId;
      if (
        currentId &&
        !results.some((w: any) => w.workOrderProjectId === currentId)
      ) {
        const existingInjected = state.dropdownWorkOrder.find(w => w.workOrderProjectId === currentId);
        const currentItem = (existingInjected && existingInjected.workOrderName && existingInjected.workOrderName !== currentId)
          ? existingInjected
          : {
              workOrderProjectId: currentId,
              workOrderName: state.taskInfo.workOrderName || currentId,
            };
        state.dropdownWorkOrder = [currentItem, ...results];
      } else {
        state.dropdownWorkOrder = results;
      }
    } finally {
      state.loadingWorkOrder = false;
    }
  };

  // Pass isNonProductive callback to hide field service for overhead lines
  const isNonProductive = () => state.taskInfo.overHead;
  const {
    allowInputFieldService,
    sigOption,
    fieldServiceRules,
    uploadSignatures,
  } = useFieldService(state, isNonProductive);

  const rules = {
    siteProjectId: [
      {
        required: true,
        message: "Please select Site",
        trigger: ["blur", "change"],
      },
    ],
    assetProjectId: [
      createConditionalValidator(
        () => !state.taskInfo.overHead,
        "Please select Fleet",
        ["blur", "change"],
      ),
    ],
    workOrderProjectId: [
      createConditionalValidator(
        () => !state.taskInfo.overHead,
        "Please select Work Order",
        ["blur", "change"],
      ),
    ],
    comments: [
      {
        required: true,
        message: "Please enter Job Description",
        trigger: ["blur", "change"],
      },
    ],

    ...fieldServiceRules,
  };

  const loadTasksForOverhead = async () => {
    state.loadingWorkOrderTask = true;
    state.error = null;
    try {
      state.dropdownWorkOrderTask =
        await dropdownLoaders.loadOverheadTaskCodes();
    } catch (error) {
      state.error = {
        message:
          error instanceof Error
            ? error.message
            : "Failed to load overhead tasks",
      };
    } finally {
      state.loadingWorkOrderTask = false;
    }
  };

  const setOverhead = (value: boolean) => {
    state.taskInfo.overHead = value;

    if (value) {
      state.taskInfo.assetProjectId = undefined;
      state.taskInfo.assetName = undefined;
      state.taskInfo.workOrderProjectId = undefined;
      state.taskInfo.workOrderName = undefined;
      state.taskInfo.workOrderDescription = undefined;
      state.taskInfo.npCode = undefined;
      state.taskInfo.npCodeName = undefined;
      loadTasksForOverhead();
    }
  };

  const setSite = (siteProjectId: string) => {
    const site = state.dropdownSite.find(
      (s) => s.siteProjectId === siteProjectId,
    );
    if (site) {
      state.taskInfo.siteProjectId = siteProjectId;
      state.taskInfo.siteName = site.siteName;
      state.taskInfo.companyId = site.companyId;
      // Clear dependent fields (fleet/workorder) but NOT overhead task codes (npCode is global)
      state.taskInfo.assetProjectId = undefined;
      state.taskInfo.assetName = undefined;
      state.taskInfo.workOrderProjectId = undefined;
      state.taskInfo.workOrderName = undefined;
      state.taskInfo.workOrderDescription = undefined;

      // Clear dropdown options for dependent fields
      state.dropdownFleet = [];
      state.dropdownWorkOrder = [];

      if (state.taskInfo.overHead) {
        loadTasksForOverhead();
      } else {
        loadEquipmentsFromApi(siteProjectId);
      }
    } else if (
      siteProjectId === undefined ||
      siteProjectId === null ||
      siteProjectId === ""
    ) {
      state.taskInfo.siteProjectId = undefined;
      state.taskInfo.siteName = undefined;
      state.taskInfo.companyId = undefined;
      state.taskInfo.assetProjectId = undefined;
      state.taskInfo.assetName = undefined;
      state.taskInfo.workOrderProjectId = undefined;
      state.taskInfo.workOrderName = undefined;
      state.taskInfo.workOrderDescription = undefined;

      // Load all equipments when site is cleared
      loadEquipmentsFromApi();
      // Also load all work orders? Or clear them?
      // Requirement says: "allow fetching all work orders if neither site nor fleet is selected"
      // So if we clear site, we effectively enable fetching all WOs if fleet is also empty.
      loadWorkOrdersFromApi();
    }
  };

  const setFleet = async (assetProjectId: string) => {
    let fleet = state.dropdownFleet.find(
      (f) => f.assetProjectId === assetProjectId,
    );

    if (fleet) {
      state.taskInfo.assetProjectId = fleet.assetProjectId;
      state.taskInfo.assetName = fleet.assetName;

      state.taskInfo.workOrderProjectId = undefined;
      state.taskInfo.workOrderName = undefined;
      state.taskInfo.workOrderDescription = undefined;
      state.taskInfo.npCode = undefined;
      state.taskInfo.npCodeName = undefined;

      // Clear work order dropdown when fleet changes
      state.dropdownWorkOrder = [];

      const sitePid = fleet.parentProjectId
        ? siteProjectFromAny(fleet.parentProjectId)
        : undefined;
      if (sitePid && state.taskInfo.siteProjectId !== sitePid) {
        try {
          const site = await injectSiteIfNeeded(sitePid);
          if (site) {
            state.taskInfo.siteProjectId = site.siteProjectId;
            state.taskInfo.siteName = site.siteName;
          }
        } catch (error) {
          // Error mapping site
        }
      }

      loadWorkOrdersFromApi(fleet.assetProjectId);
    } else if (
      assetProjectId === undefined ||
      assetProjectId === null ||
      assetProjectId === ""
    ) {
      state.taskInfo.assetProjectId = undefined;
      state.taskInfo.assetName = undefined;
      state.taskInfo.workOrderProjectId = undefined;
      state.taskInfo.workOrderName = undefined;
      state.taskInfo.workOrderDescription = undefined;
      state.taskInfo.npCode = undefined;
      state.taskInfo.npCodeName = undefined;

      // Load all work orders when fleet is cleared
      loadWorkOrdersFromApi();
    }
  };

  const setWorkOrder = async (workOrderProjectId: string) => {
    const workOrder = state.dropdownWorkOrder.find(
      (w) => w.workOrderProjectId === workOrderProjectId,
    );
    if (workOrder) {
      state.taskInfo.workOrderProjectId = workOrderProjectId;
      state.taskInfo.workOrderName = workOrder.workOrderName;
      state.taskInfo.workOrderDescription = workOrder.description;

      state.taskInfo.npCode = undefined;
      state.taskInfo.npCodeName = undefined;

      // Reverse mapping: derive Fleet and Site from Work Order hierarchy
      try {
        const woPid: string | undefined = (workOrder as any)?.parentProjectId;
        const assetPid = woPid ? equipmentProjectFromAny(woPid) : undefined;

        if (assetPid && state.taskInfo.assetProjectId !== assetPid) {
          try {
            const sid = siteProjectFromAny(assetPid);
            const equip = await injectAssetIfNeeded(sid, assetPid);
            if (equip) {
              state.taskInfo.assetProjectId = equip.assetProjectId;
              state.taskInfo.assetName = equip.assetName;
            }
          } catch (error) {
            // Error mapping fleet
          }
        }

        const currentAssetPid = state.taskInfo.assetProjectId || assetPid;
        const derivedSitePid = currentAssetPid
          ? siteProjectFromAny(currentAssetPid)
          : undefined;

        if (derivedSitePid && state.taskInfo.siteProjectId !== derivedSitePid) {
          try {
            const site = await injectSiteIfNeeded(derivedSitePid);
            if (site) {
              state.taskInfo.siteProjectId = site.siteProjectId;
              state.taskInfo.siteName = site.siteName;
            }
          } catch (error) {
            // Error mapping site
          }
        }
      } catch (error) {
        // Error getting details
      }
    }
  };

  const setTask = (npCode: string) => {
    const task = state.dropdownWorkOrderTask.find((t) => t.npCode === npCode);
    if (task) {
      state.taskInfo.npCode = npCode;
      state.taskInfo.npCodeName = task.npCodeName;
    }
  };

  const setJobDescription = (description: string) => {
    state.taskInfo.comments = description;
  };

  const setFieldService = (enabled: boolean) => {
    state.taskInfo.additionInfo = enabled;
  };

  const injectSiteIfNeeded = async (siteId: string) => {
    if (!siteId) return null;

    // Check if already in dropdown and has a proper name
    const existing = state.dropdownSite.find((s) => s.siteProjectId === siteId);
    if (existing && existing.siteName && existing.siteName !== siteId)
      return existing;

    // Fetch by ID via store action
    try {
      const result = await store.dispatch("v2/fetchSiteById", siteId);
      if (result) {
        const siteItem = {
          siteProjectId: result.id || result.projectId,
          siteName: result.name,
          companyId: result.companyId,
        };
        // Inject at the beginning
        state.dropdownSite = [
          siteItem,
          ...state.dropdownSite.filter((s) => s.siteProjectId !== siteId),
        ];
        return siteItem;
      }
    } catch (err) {
      // Error injecting site
    }
    return null;
  };

  const injectAssetIfNeeded = async (siteId: string, assetId: string) => {
    if (!siteId || !assetId) return null;

    // Check if already in dropdown and has a proper name
    const existing = state.dropdownFleet.find(
      (a) => a.assetProjectId === assetId,
    );
    if (existing && existing.assetName && existing.assetName !== assetId)
      return existing;

    // Fetch by ID via store action
    try {
      const result = await store.dispatch("v2/fetchAssetById", {
        siteId,
        assetId,
      });
      if (result) {
        const assetItem = {
          assetProjectId: result.id || result.projectId,
          assetName: result.name,
          parentProjectId: result.parentProjectId,
        };
        // Inject at the beginning
        state.dropdownFleet = [
          assetItem,
          ...state.dropdownFleet.filter((a) => a.assetProjectId !== assetId),
        ];
        return assetItem;
      }
    } catch (err) {
      // Error injecting asset
    }
    return null;
  };

  const injectWorkOrderIfNeeded = async (
    assetId: string,
    workOrderId: string,
  ) => {
    if (!assetId || !workOrderId) return null;

    // Check if already in dropdown and has a proper name
    const existing = state.dropdownWorkOrder.find(
      (w) => w.workOrderProjectId === workOrderId,
    );
    if (
      existing &&
      existing.workOrderName &&
      existing.workOrderName !== workOrderId
    )
      return existing;

    // Fetch by ID via store action
    try {
      const result = await store.dispatch("v2/fetchWorkOrderById", {
        assetId,
        workOrderId,
      });
      if (result) {
        const workOrderItem = {
          workOrderProjectId: result.id || result.projectId,
          workOrderName: result.name,
          description: result.description,
          parentProjectId: result.parentProjectId,
        };
        // Inject at the beginning
        state.dropdownWorkOrder = [
          workOrderItem,
          ...state.dropdownWorkOrder.filter(
            (w) => w.workOrderProjectId !== workOrderId,
          ),
        ];
        return workOrderItem;
      }
    } catch (err) {
      // Error injecting work order
    }
    return null;
  };

  // Don't auto-load sites here - it causes race conditions with preloadDropdowns.
  // Sites will be loaded when preloadDropdowns runs or when user opens the dropdown.

  return {
    state,
    rules,
    allowInputFieldService,
    sigOption,
    loadSitesFromApi,
    searchSites,
    loadEquipmentsFromApi,
    searchEquipments,
    loadWorkOrdersFromApi,
    searchWorkOrders,
    setOverhead,
    setSite,
    setFleet,
    setWorkOrder,
    setTask,
    setJobDescription,
    setFieldService,
    searchTasks: async (query: string) => {
      if (state.taskInfo.overHead) {
        state.loadingWorkOrderTask = true;
        try {
          state.dropdownWorkOrderTask =
            await dropdownLoaders.searchOverheadTaskCodes(query);
        } finally {
          state.loadingWorkOrderTask = false;
        }
      }
    },
    loadTasksFromApi: loadTasksForOverhead,
    uploadSignatures,
    // Inject functions for edit mode
    injectSiteIfNeeded,
    injectAssetIfNeeded,
    injectWorkOrderIfNeeded,
  };
}
